import { useMemo } from 'react';
import { InvoicePaymentDisplayStatusType } from 'anchor-scout-common/types';
import colors from 'seagull/foundations/colors.module.scss';
import GenericError from "../../../components/GenericError/GenericError";
import { routes } from "../../../constants";
import { useRestfulWrapper } from "../../../hooks/useRestfulWrapper";
import { useLocaleKeys } from "../../../i18n";
import { InvoicesDigestModel } from "../../../models/invoices-digest/InvoicesDigestModel";
import GraphWidgetBottomCard from "../GraphWidget/GraphWidgetBottomCard";
import GraphWidgetLayout from "../GraphWidget/GraphWidgetLayout";
import InvoiceBar from "./InvoiceBar";
import InvoicesInfoTable from "./InvoicesDigestTable";
import { useSubscribeToBillingObservable } from "../../../providers/BillingObservableProvider";
import { useStore, useStoreActions } from "../../../store/store-context";
import pluralize from "../../../utils/pluralize";
import ThreeLineTopCard from "../GraphWidget/ThreeLineTopCard";
import styles from "./InvoicesDigestWidget.module.scss";
import { invoicePaymentStatusMap } from "./InvoicesDigestWIdget.utils";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const makeNewInvoiceStatusGroup = (count, amount) => {
  return {
    count,
    amount: {
      amount
    }
  };
};
export function mockInvoiceDigestApi() {
  let {
    hasCreatedInvoice
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
    hasCreatedInvoice: true
  };
  return {
    statuses: {
      requiresClientApproval: makeNewInvoiceStatusGroup(10, 1500000),
      paymentsVerificationRequired: makeNewInvoiceStatusGroup(10, 1500000),
      missingPaymentMethod: makeNewInvoiceStatusGroup(10, 1500000),
      failed: makeNewInvoiceStatusGroup(10, 1500000),
      disputed: makeNewInvoiceStatusGroup(10, 1500000),
      paymentStopped: makeNewInvoiceStatusGroup(0, 0),
      paymentMethodUnverified: makeNewInvoiceStatusGroup(10, 1500000)
    },
    processing: makeNewInvoiceStatusGroup(123, 12300),
    requiresAttention: makeNewInvoiceStatusGroup(123, 12300),
    hasCreatedInvoice: !!hasCreatedInvoice
  };
}
function MockInvoicesDigestBottomCard() {
  const invoiceDigest = useMemo(() => InvoicesDigestModel.fromDTO(mockInvoiceDigestApi()), []);
  return renderBottomCardContent(invoiceDigest);
}
const renderBottomCardContent = invoicesDigest => /*#__PURE__*/_jsxs("div", {
  className: styles.bottomCardContent,
  children: [/*#__PURE__*/_jsx(InvoiceBar, {
    invoicesDigest: invoicesDigest
  }), /*#__PURE__*/_jsx(InvoicesInfoTable, {
    invoiceDigestModel: invoicesDigest
  })]
});
export const invoiceStatusToColor = {
  requiresClientApproval: '#6A9FF0',
  paymentsVerificationRequired: colors.purple300,
  missingPaymentMethod: '#A9E5F2',
  failed: '#FDA5EF',
  disputed: colors.red700,
  paymentStopped: '#e7049e',
  paymentMethodUnverified: '#FBD499'
};
const InvoicesDigestWidget = _ref => {
  let {
    invoicesDigest,
    isRefreshing,
    isFirstTimeLoading
  } = _ref;
  const {
    t
  } = useLocaleKeys('pages.dashboard.invoicesDigestWidget');
  const testIdPrefix = 'invoices-digest';
  const paymentStatus = 'paymentStatus';
  return /*#__PURE__*/_jsxs(GraphWidgetLayout, {
    loading: isRefreshing,
    title: t.title(),
    bottomHeight: "l",
    children: [/*#__PURE__*/_jsx(ThreeLineTopCard, {
      loading: isFirstTimeLoading,
      title: t.processing.title(),
      success: true,
      testId: "".concat(testIdPrefix, "-processing"),
      titleInfo: /*#__PURE__*/_jsx("div", {
        "data-testid": "".concat(testIdPrefix, "-processing-tooltip"),
        children: t.processing.tooltip()
      }),
      secondaryText: pluralize('invoice', invoicesDigest.processing().count, true),
      primaryText: invoicesDigest.processing().amount.display(),
      linkTo: "".concat(routes.invoicesDashboard, "?").concat(paymentStatus, "=").concat(InvoicePaymentDisplayStatusType.Processing, ",").concat(InvoicePaymentDisplayStatusType.Collected)
    }), /*#__PURE__*/_jsx(ThreeLineTopCard, {
      loading: isFirstTimeLoading,
      title: t.requireAttention.title(),
      testId: "".concat(testIdPrefix, "-require-attention"),
      secondaryText: pluralize('invoice', invoicesDigest.requiresAttention().count, true),
      titleInfo: /*#__PURE__*/_jsx("div", {
        "data-testid": "".concat(testIdPrefix, "-require-attention-tooltip"),
        children: t.requireAttention.tooltip()
      }),
      primaryText: invoicesDigest.requiresAttention().amount.display(),
      linkTo: "".concat(routes.invoicesDashboard, "?").concat(paymentStatus, "=").concat(Object.values(invoicePaymentStatusMap).join(','))
    }), /*#__PURE__*/_jsx(GraphWidgetBottomCard, {
      testId: "invoices-digest-bottom-card",
      loadingOrEmptyBg: () => !invoicesDigest.hasCreatedInvoice() || isFirstTimeLoading ? /*#__PURE__*/_jsx(MockInvoicesDigestBottomCard, {}) : null,
      loading: isFirstTimeLoading,
      loadingMsg: t.loadingMsg(),
      empty: invoicesDigest.numInvoiceTypes() === 0,
      emptyMsg: invoicesDigest.hasCreatedInvoice() ? /*#__PURE__*/_jsx("div", {
        "data-testid": "invoice-ever-created-covering-text",
        children: t.noAttentionRequired()
      }) : /*#__PURE__*/_jsx("div", {
        "data-testid": "never-created-invoice-covering-text",
        children: t.viewAttentionRequired()
      }),
      children: renderBottomCardContent(invoicesDigest)
    })]
  });
};
export default function InvoicesDigestWidgetFetch() {
  const {
    fetchInvoicesDigest
  } = useStoreActions();
  const initialData = useStore(state => state.invoicesDigest.data);
  const {
    refresh,
    renderRestfulState
  } = useRestfulWrapper({
    initialData: initialData,
    fetch: () => fetchInvoicesDigest()
  });
  useSubscribeToBillingObservable(async () => {
    refresh();
  });
  return renderRestfulState({
    renderChild: _ref2 => {
      let {
        data,
        loading: isRefreshing
      } = _ref2;
      return /*#__PURE__*/_jsx(InvoicesDigestWidget, {
        invoicesDigest: data,
        isRefreshing: isRefreshing,
        isFirstTimeLoading: false
      });
    },
    renderLoader: () => /*#__PURE__*/_jsx(InvoicesDigestWidget, {
      invoicesDigest: InvoicesDigestModel.fromDTO(mockInvoiceDigestApi()),
      isRefreshing: true,
      isFirstTimeLoading: true
    }),
    renderError: () => /*#__PURE__*/_jsx(GenericError, {
      testId: "widget-error"
    })
  });
}